import { useMemo } from 'react';

const BreathingBar = ({ 
  phase, 
  progress, 
  phases = ['inhale', 'exhale'],
  phaseDurations = { inhale: 4, exhale: 4 },
  colors = {
    inhale: '#22d3ee',
    exhale: '#3b82f6',
    hold: '#a855f7',
    'hold-in': '#a855f7',
    'hold-out': '#a855f7',
    ready: '#94a3b8'
  },
  lightTheme = false
}) => {
  // Calculate the fill level (0 to 1) based on phase and progress
  const fillLevel = useMemo(() => {
    if (phase === 'ready') return 0.05; // Small initial fill
    
    if (phase === 'inhale') {
      // Rising from bottom to top (0% to 100%)
      return progress;
    } else if (phase === 'exhale') {
      // Falling from top to bottom (100% to 0%)
      return 1 - progress;
    } else if (phase === 'hold' || phase === 'hold-in') {
      // Hold at top
      return 1;
    } else if (phase === 'hold-out') {
      // Hold at bottom
      return 0.05;
    }
    return 0.5;
  }, [phase, progress]);

  // Current color based on phase
  const currentColor = colors[phase] || colors.ready;
  
  // Secondary color for gradient
  const secondaryColor = useMemo(() => {
    if (phase === 'inhale') return '#06b6d4'; // cyan-500
    if (phase === 'exhale') return '#1d4ed8'; // blue-700
    if (phase === 'hold' || phase === 'hold-in' || phase === 'hold-out') return '#7c3aed'; // violet-600
    return '#64748b'; // slate-500
  }, [phase]);

  // Phase text
  const phaseText = useMemo(() => {
    if (phase === 'ready') return 'Prêt';
    if (phase === 'inhale') return 'Inspirez';
    if (phase === 'exhale') return 'Expirez';
    if (phase === 'hold' || phase === 'hold-in' || phase === 'hold-out') return 'Retenez';
    return phase;
  }, [phase]);

  // Remaining time
  const remainingTime = phase !== 'ready' 
    ? Math.ceil((1 - progress) * (phaseDurations[phase] || 0))
    : null;

  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center">
      {/* Main container */}
      <div className={`relative w-24 sm:w-28 h-64 sm:h-80 rounded-full overflow-hidden backdrop-blur-sm shadow-2xl ${lightTheme ? 'bg-white/60 border-2 border-slate-600/70' : 'bg-white/10 border border-white/20'}`}>
        
        {/* Background glow effect */}
        <div 
          className="absolute inset-0 rounded-full opacity-30 transition-all duration-300"
          style={{
            background: `radial-gradient(ellipse at center bottom, ${currentColor}40, transparent 70%)`
          }}
        />
        
        {/* Animated fill — height is updated directly by the parent at
            ~60 FPS (requestAnimationFrame) so we deliberately DO NOT apply
            a CSS transition on height. A transition here fights the per-
            frame JS updates and causes visible saccades. Colors / shadows
            keep a short transition for phase changes only. */}
        <div 
          className="absolute bottom-0 left-0 right-0 rounded-b-full transition-[background,box-shadow] duration-150 ease-out"
          style={{
            height: `${fillLevel * 100}%`,
            background: `linear-gradient(to top, ${secondaryColor}, ${currentColor})`,
            boxShadow: `0 0 30px ${currentColor}60, inset 0 0 20px ${currentColor}30`
          }}
        >
          {/* Animated bubbles/particles inside */}
          <div className="absolute inset-0 overflow-hidden rounded-b-full">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white/30 animate-pulse"
                style={{
                  width: `${4 + (i % 3) * 2}px`,
                  height: `${4 + (i % 3) * 2}px`,
                  left: `${15 + (i * 12)}%`,
                  bottom: `${10 + (i * 15)}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${1.5 + (i % 2) * 0.5}s`
                }}
              />
            ))}
          </div>
          
          {/* Top surface shine */}
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-2 rounded-full bg-white/40 blur-sm"
          />
        </div>
        
        {/* Glass reflection overlay */}
        <div 
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
          }}
        />
        
        {/* Side highlight */}
        <div 
          className="absolute left-2 top-4 bottom-4 w-3 rounded-full bg-white/10 blur-sm"
        />
        
        {/* Measurement markers */}
        <div className="absolute right-3 top-4 bottom-4 flex flex-col justify-between">
          {[...Array(5)].map((_, i) => (
            <div 
              key={i}
              className="w-2 h-0.5 bg-white/30 rounded-full"
            />
          ))}
        </div>
      </div>

      {/* Phase indicator */}
      <div className="text-center mt-6 sm:mt-8">
        <span 
          className="text-4xl sm:text-5xl font-bold drop-shadow-lg transition-colors duration-300"
          style={{ color: currentColor }}
        >
          {phaseText}
        </span>
        {remainingTime !== null && (
          <span className={`text-2xl sm:text-3xl ml-3 ${lightTheme ? 'text-slate-700' : 'text-white/70'}`}>
            {remainingTime}s
          </span>
        )}
      </div>
      
      {/* Progress bar (optional subtle indicator) */}
      <div className={`w-48 h-1 mt-4 rounded-full overflow-hidden ${lightTheme ? 'bg-slate-300/60' : 'bg-white/10'}`}>
        <div 
          className="h-full rounded-full transition-all duration-100"
          style={{
            width: `${fillLevel * 100}%`,
            background: `linear-gradient(to right, ${secondaryColor}, ${currentColor})`
          }}
        />
      </div>
    </div>
  );
};

export default BreathingBar;
